import { useState } from "react";
import { Alert, Badge, Button, Card, Field, Input, Modal } from "../../ds";
import type { WgOverview, WgServerConfig } from "../../lib/api";
import { relSeconds } from "../../lib/format";
import { CopyValue } from "../CopyValue";
import { KeyValues } from "../KeyValues";

// Der Konzentrator selbst: Soll aus der Datenbank, Ist aus der Statusdatei des
// Sync-Dienstes (docs/wireguard-server.md).
//
// Beides in einer Karte und bewusst nebeneinander: die interessante Frage ist
// nie „was ist eingestellt" oder „was läuft", sondern ob beides dasselbe sagt.

interface Props {
  data: WgOverview;
  onSave: (patch: Partial<WgServerConfig>) => Promise<void>;
}

/** Warnung, wenn der gemessene Zustand nichts taugt — sonst nichts. */
function StatusHinweis({ data }: { data: WgOverview }) {
  const { status } = data;
  if (!status.available) {
    return (
      <Alert variant="warning">
        Kein Sync-Dienst auf diesem Server{status.reason ? ` (${status.reason})` : ""}. Der Bestand
        unten wird gepflegt und nach <code>peers.conf</code> geschrieben, aber niemand wendet ihn
        an. Einrichtung: <code>docs/wireguard-server.md</code>.
      </Alert>
    );
  }
  if (status.stale) {
    return (
      <Alert variant="warning">
        Der gemessene Zustand ist {relSeconds(status.ageSecs)} alt — der Sync-Dienst meldet sich
        nicht. Handshakes und Durchsatz unten sind entsprechend alt.{" "}
        <code>systemctl status eul-wg-sync.timer</code>
      </Alert>
    );
  }
  return null;
}

export function WgServerPanel({ data, onSave }: Props) {
  const [form, setForm] = useState<WgServerConfig | null>(null);
  const [busy, setBusy] = useState(false);
  const { server, status } = data;

  const aktiv = data.peers.filter((p) => p.enabled).length;
  const laeuft = status.available && server.up;

  return (
    <>
      <StatusHinweis data={data} />

      <Card
        title="Server"
        actions={
          <Button size="sm" variant="ghost" iconName="edit" onClick={() => setForm({ ...server })}>
            Bearbeiten
          </Button>
        }
      >
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant={laeuft ? "success" : "warn"} dot>
              {!status.available
                ? "kein Sync-Dienst"
                : server.up
                  ? "Interface up"
                  : "Interface down"}
            </Badge>
            <Badge variant="neutral">{aktiv} aktive Peers</Badge>
            {status.available && (
              <Badge variant={status.stale ? "warn" : "neutral"}>
                Stand: {relSeconds(status.ageSecs)}
              </Badge>
            )}
          </div>

          <KeyValues
            items={[
              ["Interface", server.interface],
              ["Port", server.listenPort],
              ["Adresse im Tunnel", server.address],
              // Der Sollwert, gegen den die Uplink-Tabelle misst. Vor dieser
              // Seite gab es ihn nirgends — sie erschloss ihn aus der Mehrheit.
              ["Endpoint für die Router", server.endpoint],
              [
                "Public Key",
                server.publicKey ? (
                  <CopyValue value={server.publicKey} />
                ) : (
                  <span className="text-content-3">meldet der Sync-Dienst</span>
                ),
              ],
            ]}
          />
        </div>
      </Card>

      <Modal
        open={form !== null}
        onClose={() => setForm(null)}
        title="WireGuard-Server bearbeiten"
        subtitle="Ändert die Server-Konfiguration. Die Router merken davon erst etwas, wenn ihr Uplink darauf zeigt."
        actions={
          <>
            <Button variant="ghost" onClick={() => setForm(null)}>
              Abbrechen
            </Button>
            <Button
              variant="primary"
              iconName="check"
              disabled={busy}
              onClick={() => {
                if (!form) return;
                setBusy(true);
                void onSave(form)
                  .then(() => setForm(null))
                  .finally(() => setBusy(false));
              }}
            >
              Speichern
            </Button>
          </>
        }
      >
        {form && (
          <div className="flex flex-col gap-3">
            <Alert variant="neutral">
              Interface-Name und Adresse zieht der Sync-Dienst mit einem kurzen <code>down/up</code>{" "}
              nach — laufende Tunnel brechen dabei ab. Port und Endpoint kosten nichts.
            </Alert>
            <Field label="Interface">
              <Input
                value={form.interface}
                onChange={(e) => setForm({ ...form, interface: e.target.value })}
              />
            </Field>
            <Field label="Port">
              <Input
                type="number"
                value={String(form.listenPort)}
                onChange={(e) => setForm({ ...form, listenPort: Number(e.target.value) })}
              />
            </Field>
            <Field label="Adresse im Tunnel" helperText="mit Präfix, z.B. 10.88.0.1/16">
              <Input
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
              />
            </Field>
            <Field
              label="Endpoint für die Router"
              helperText="Sollwert — daran misst die Uplink-Tabelle, welcher Router abweicht"
            >
              <Input
                value={form.endpoint}
                onChange={(e) => setForm({ ...form, endpoint: e.target.value })}
              />
            </Field>
          </div>
        )}
      </Modal>
    </>
  );
}
